import { sha256 } from 'js-sha256';

import { LOGGED_OUT_USER_ID_LOCAL_STORAGE_KEY } from 'constants/session';
import type { MarketEntity } from 'queries/marketEntityTransform';
import { getLocalStorageWithExpiration, setLocalStorageWithExpiration } from 'utils/storage';
import { uuidv4 } from 'utils/uuid';

import googleAnalytics from './providers/googleAnalytics';

export interface AnalyticsProviderInterface {
    sendEvent: (event: string, content?: any) => void;
}

const analyticsProviders: AnalyticsProviderInterface[] = [googleAnalytics];

const CID_STORAGE_KEY = 'kalshi_cid';
const CID_EXPIRY_DAYS = 30; // Store cid for 30 days
const UUID_REGEX = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;

export const storeCid = (cid: string): void => {
    if (cid && typeof window !== 'undefined') {
        setLocalStorageWithExpiration(CID_STORAGE_KEY, cid, CID_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    }
};

export const getStoredCid = (): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    return getLocalStorageWithExpiration(CID_STORAGE_KEY);
};

/**
 * Get or create a persistent logged-out UUID for attribution.
 */
export const getOrCreateLoggedOutUserId = (): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        if (!window.localStorage || typeof window.localStorage.getItem !== 'function') {
            return null;
        }

        const existing = window.localStorage.getItem(LOGGED_OUT_USER_ID_LOCAL_STORAGE_KEY);
        if (existing && UUID_REGEX.test(existing)) {
            return existing;
        }

        const newId = uuidv4();
        window.localStorage.setItem(LOGGED_OUT_USER_ID_LOCAL_STORAGE_KEY, newId);
        return newId;
    } catch {
        return null;
    }
};

const sendEvent = (event: string, content?: any) => {
    for (const provider of analyticsProviders) {
        provider.sendEvent(event, content);
    }
};

export default { sendEvent } as AnalyticsProviderInterface;

const getMetaContentId = (seriesTicker: string, marketYesSubtitle: string): string => {
    const validSeriesTickers = ['KXNFLGAME', 'KXNBAGAME', 'KXNCAAFGAME', 'KXUFCFIGHT'];
    if (validSeriesTickers.includes(seriesTicker)) {
        return `${seriesTicker}-${marketYesSubtitle.toLowerCase()}`;
    }
    return '';
};

export const getMetaContentIdsFromEvent = (markets: MarketEntity[], seriesTicker?: string): string[] => {
    const contentIds: string[] = [];
    for (const market of markets) {
        const metaContentId = getMetaContentId(seriesTicker ?? '', market.yesSubtitle ?? '');
        if (metaContentId) {
            contentIds.push(metaContentId);
        }
    }
    return contentIds;
};

export const hashUserEmail = (email: string): string => {
    return sha256(email.trim().toLowerCase());
};

export const normalizePhone = (phone: string): string => {
    return phone.replace(/\D/g, '');
};

export const normalizeBirthdate = (date: Date): string => {
    // Format as YYYYMMDD (e.g., 19910526 for May 26, 1991)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
};
